import { ref, computed, onMounted } from 'vue'
import { logger } from '@/utils/logger'

/**
 * Composable for detecting and managing embed/iframe mode
 * Use this to conditionally show/hide UI elements when app is embedded in Dayforce
 */

// Singleton state - shared across all component instances
const isInIframe = ref(false)
const detectedParentOrigin = ref(null)
const detectedParentHostname = ref(null)
const isInitialized = ref(false)

export function getEmbedParentInfo(referrer) {
  let parentOrigin = 'unknown'
  let parentHostname = 'unknown'
  const embedReferrer = referrer ?? (typeof document !== 'undefined' ? document.referrer : '')

  try {
    if (embedReferrer) {
      const referrerUrl = new URL(embedReferrer)
      parentOrigin = referrerUrl.origin
      parentHostname = referrerUrl.hostname
    }
  } catch (error) {
    logger.warn('Could not determine parent origin', error.message)
  }

  return { parentOrigin, parentHostname }
}

/**
 * Detect if running in iframe and identify parent origin
 */
function detectEmbedMode() {
  if (isInitialized.value) return

  if (typeof window === 'undefined') return

  // Check if in iframe
  isInIframe.value = window.self !== window.top

  if (isInIframe.value) {
    const { parentOrigin, parentHostname } = getEmbedParentInfo()
    detectedParentOrigin.value = parentOrigin
    detectedParentHostname.value = parentHostname
  }

  isInitialized.value = true

  // Log for debugging in development only to avoid noise/leaking origins
  if (import.meta.env.MODE === 'development') {
    logger.info('[EMBED MODE]', {
      isInIframe: isInIframe.value,
      parentOrigin: detectedParentOrigin.value,
      parentHostname: detectedParentHostname.value
    })
  }
}

/**
 * Retrieve embed status without needing component lifecycle hooks.
 * Initializes detection on first use.
 */
export function getEmbedStatus() {
  if (!isInitialized.value) {
    detectEmbedMode()
  }

  return {
    isEmbedded: isInIframe.value,
    parentOrigin: detectedParentOrigin.value,
    parentHostname: detectedParentHostname.value,
    isTrustedEmbed: isInIframe.value
  }
}

/**
 * Evaluate embed trust status. Every embed is trusted — framing is restricted
 * by the CSP frame-ancestors policy. Route-specific destination checks should
 * be implemented by the caller.
 *
 * @returns {{ isEmbedded: boolean, isTrusted: boolean }}
 */
export function getEmbedTrustStatus() {
  const { isEmbedded, isTrustedEmbed } = getEmbedStatus()

  if (!isEmbedded) {
    return { isEmbedded: false, isTrusted: false }
  }

  return { isEmbedded: true, isTrusted: isTrustedEmbed }
}

export function useEmbedMode() {
  // Initialize on first use
  onMounted(() => {
    detectEmbedMode()
  })

  /**
   * Is the app currently running in an iframe?
   */
  const isEmbedded = computed(() => isInIframe.value)

  /**
   * Alias of isEmbedded — every embed is treated as trusted; framing is
   * restricted by the CSP frame-ancestors policy, not by client-side checks.
   */
  const isTrustedEmbed = computed(() => isInIframe.value)

  return {
    isEmbedded,
    isTrustedEmbed
  }
}
